/**
 * The welcome-pack perk block — rendered INSIDE the invite's pricing section,
 * not as a chapter of its own.
 *
 * The invite sells the website; the pack is one of the reasons to say yes to
 * the monthly price, so it lives with the price (decided with the page's
 * design pass, 2026-08-31; sourcing decisions in docs/welcome-pack-sourcing.md).
 *
 * 2026-09-09 (fnd-80r): the fnd-7dx static illustration is now the LAST
 * resort. The block first tries to show the actual pack pieces — the real
 * business-card pair (front + back, `@foundlio/pack` `CardFront`/`CardBack`)
 * and the car magnet (`CarMagnet`) — wearing the prospect's own logo, brand
 * colour and fonts, exactly as `/lab/pack` renders them. Flat on the paper
 * background: no photo plate, no scene, no props (that composite is what
 * broke on real invites and got pulled in fnd-7dx).
 *
 * The photographed mockup (PackShowcase, fnd-dzz) was tried below the flat
 * pieces and pulled again the same day — the invite keeps the flat pieces
 * only; the photo scene lives on the dashboard's print-kit page (fnd-gt9).
 * Its own error boundary is separate from the flat-pieces one: a failure in
 * the photo scene only drops the scene, never the flat pieces above it.
 *
 * Identity source, in order:
 *   1. `GET /api/invite/:token/identity` (real `SiteIdentity`, needs the
 *      backend) — demo tokens skip this, there is no bundle behind them.
 *      Retried a few times with backoff: the first open of a freshly
 *      deployed invite can race the R2 upload by seconds, and a 404/503 in
 *      that window used to pin the fallback for the whole visit.
 *   2. A fallback identity built from the invite itself (`inviteIdentity`):
 *      the prospect's real name, trade, city and domain, no logo, ink brand —
 *      the card prints their name in type. Used as the initial paint (so
 *      nothing flashes while #1 is in flight) and whenever #1 never lands.
 *      A REAL invite must never show another business's card: before this
 *      the sample (Republic Plumbing) stood in here, and a prospect who
 *      opened their invite while #1 failed saw a plumber's brand on "their"
 *      welcome pack. `SAMPLE_PACK_IDENTITY` is now demo-only.
 *      The footnote only claims "drawn from your live site" once #1 has
 *      actually landed.
 *   3. The fnd-7dx hand-built line-art illustration — only reached if the
 *      live pack components themselves throw (an `ErrorBoundary` around the
 *      render), so the block still never disappears.
 */

import { Component, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import axios from 'axios';
import { PLANS } from '../../lib/pricing';
import {
  CARD_H,
  CARD_W,
  CardBack,
  CardFront,
  CarMagnet,
  identityToPack,
  MAGNET_H,
  MAGNET_W,
  SAMPLE_PACK_IDENTITY,
  useGoogleFonts,
  type PackBusiness,
  type SiteIdentity,
} from '@foundlio/pack';

/** The tier this is bundled with, and everything above it. */
const INCLUDED_FROM = 'professional' as const;

interface Props {
  /** The invite token — fetches the live identity when present and not a demo. */
  token?: string;
  /**
   * The invite API base — `/api/invite/<token>`, or `/api/invite/me` on the
   * tokenless /claim page. fnd-mk2x: without it /claim showed the SAMPLE brand
   * (another real site's logo and phone) under the prospect's own name.
   * Defaults from `token`.
   */
  apiBase?: string;
  /** Demo tokens have no deployed bundle behind them; skip the fetch. */
  isDemo?: boolean;
  businessName: string;
  /** Human trade label, e.g. "Plumbing" — already resolved by the caller. */
  tradeName?: string;
  city?: string;
  /** The site's preview domain (`<slug>.foundlio.com`) — what the fallback card prints. */
  domain?: string;
  /** The owner as a person, when one was enriched. */
  owner?: string;
}

/**
 * The card a real invite paints before (or instead of) the live identity:
 * the prospect's own name, trade, city and domain, in ink, with no logo. The
 * pack's `identityToPack` turns a null palette into ink and a missing logo
 * into the name set in type — an honest, unbranded card, never someone
 * else's brand.
 */
function inviteIdentity(p: Pick<Props, 'businessName' | 'tradeName' | 'city' | 'domain'>): SiteIdentity {
  const domain = p.domain || 'your-business.foundlio.com';
  return {
    slug: domain.split('.')[0] || 'invite',
    domain,
    business: {
      name: p.businessName,
      phone: null,
      city: p.city ?? null,
      state: null,
      // `tradeLabel` capitalises whatever it gets; the caller's label is
      // already human ("Plumbing"), so lower-case it back into a vertical.
      vertical: p.tradeName ? p.tradeName.toLowerCase() : null,
      established: null,
      establishedIsEstimate: false,
      logo: null,
    },
    theme: { colors: null, fonts: null, logoFont: null, logoIsLogomark: false },
  };
}

/** Backoff between identity attempts — the deploy race is seconds, not minutes. */
const IDENTITY_RETRY_MS = [2_000, 6_000, 15_000];

/* ------------------------------------------------------------------ */
/* Live pack pieces — the real card pair + magnet, flat, scaled to fit  */
/* ------------------------------------------------------------------ */

/** Tracks the rendered width of `ref`'s element via ResizeObserver. */
function useAvailWidth() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [avail, setAvail] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const sync = () => setAvail(node.getBoundingClientRect().width);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);
  return [ref, avail] as const;
}

const CARD_GAP = 20;

/** The card front + back — side by side above 640px of available width,
 * stacked below it — scaled (never cropped) to whatever column it lands in. */
function LiveCardPair({ b }: { b: PackBusiness }) {
  const [ref, avail] = useAvailWidth();
  const sideBySide = avail === 0 || avail >= 640;
  const naturalW = sideBySide ? CARD_W * 2 + CARD_GAP : CARD_W;
  const naturalH = sideBySide ? CARD_H : CARD_H * 2 + CARD_GAP;
  const scale = avail > 0 ? Math.min(1, avail / naturalW) : 1;
  const stageW = naturalW * scale;
  const stageH = naturalH * scale;

  return (
    <div ref={ref} className="wo-pack-live-cards">
      <div
        className="wo-pack-live-stage"
        style={avail > 0 ? { width: stageW, height: stageH } : undefined}
      >
        <div
          className="wo-pack-live-inner"
          style={{
            width: naturalW,
            transform: `scale(${scale})`,
            flexDirection: sideBySide ? 'row' : 'column',
            gap: CARD_GAP,
          }}
        >
          <CardFront b={b} guides={false} layout="center" nameMode="auto" logoMode="auto" />
          <CardBack
            b={b}
            guides={false}
            layout="center"
            nameMode="auto"
            logoMode="auto"
            qrUrl={b.domain ? `https://${b.domain}` : null}
          />
        </div>
      </div>
      <div className="wo-pack-live-captions" style={avail > 0 ? { width: stageW } : undefined}>
        {/*
         * One caption for the whole pair, not one per card: split across two
         * spans ("Business card · front" / "· back") it wraps mid-word at a
         * 375px column and the "· back" half reads as an orphan fragment
         * with no subject. A single line never wraps that way and never
         * loses "back" its context, in either the side-by-side or the
         * stacked (<640px) layout.
         */}
        <span className="wo-pack-live-caption">Business card · front and back</span>
      </div>
    </div>
  );
}

/** The car magnet, below the cards, at a visibly smaller scale. */
function LiveMagnet({ b }: { b: PackBusiness }) {
  const [ref, avail] = useAvailWidth();
  // Reduced scale: never grows past ~72% of a full-size fit, so it reads as
  // "smaller than the cards" rather than competing with them for attention.
  const scale = avail > 0 ? Math.min(1, avail / MAGNET_W) * 0.72 : 0.5;
  const stageW = MAGNET_W * scale;
  const stageH = MAGNET_H * scale;

  return (
    <div ref={ref} className="wo-pack-live-magnet">
      <div className="wo-pack-live-stage" style={avail > 0 ? { width: stageW, height: stageH } : undefined}>
        <div className="wo-pack-live-inner" style={{ width: MAGNET_W, transform: `scale(${scale})` }}>
          <CarMagnet b={b} guides={false} nameMode="auto" logoMode="auto" />
        </div>
      </div>
      <div className="wo-pack-live-captions" style={avail > 0 ? { width: stageW } : undefined}>
        <span className="wo-pack-live-caption">Car magnet</span>
      </div>
    </div>
  );
}

function LivePack({ b, isReal }: { b: PackBusiness; isReal: boolean }) {
  useGoogleFonts([b.fonts?.heading, b.fonts?.logo]);
  return (
    <div className="wo-pack-live">
      <LiveCardPair b={b} />
      <LiveMagnet b={b} />
      {isReal ? <p className="wo-pack-live-note">Drawn from your live site</p> : null}
    </div>
  );
}

/**
 * Catches a render failure in the live pack pieces (a bad logo URL past the
 * `<img onError>` guards, a malformed identity payload, anything) and shows
 * the static illustration instead of a blank half of the pricing section.
 */
class PackErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch() {
    /* swallow — the fallback illustration is the recovery */
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/* ------------------------------------------------------------------ */
/* Final fallback — the fnd-7dx hand-built line-art illustration       */
/* ------------------------------------------------------------------ */

/** A deterministic 5×5 "scan me" grid — not a real QR, just its silhouette:
 * three finder squares in fixed corners and a fixed fill pattern so the
 * placeholder reads as a code at a glance without pretending to decode. */
const QR_FILL = [
  [1, 1, 1, 0, 1],
  [1, 0, 1, 1, 0],
  [1, 1, 0, 1, 1],
  [0, 1, 1, 0, 1],
  [1, 0, 1, 1, 1],
];

function QrGlyph() {
  const cell = 8;
  const size = cell * 5;
  return (
    <svg
      className="wo-pack-qr-svg"
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      aria-hidden="true"
    >
      <rect x={0} y={0} width={size} height={size} fill="var(--card)" />
      {QR_FILL.map((row, y) =>
        row.map((on, x) =>
          on ? (
            <rect
              key={`${x}-${y}`}
              x={x * cell}
              y={y * cell}
              width={cell}
              height={cell}
              fill="var(--ink)"
            />
          ) : null,
        ),
      )}
      {/* finder corners, drawn over the fill so they stay crisp */}
      {[
        [0, 0],
        [size - cell * 1.5, 0],
        [0, size - cell * 1.5],
      ].map(([x, y]) => (
        <rect
          key={`f-${x}-${y}`}
          x={x}
          y={y}
          width={cell * 1.5}
          height={cell * 1.5}
          fill="none"
          stroke="var(--accent)"
          strokeWidth={1.6}
        />
      ))}
    </svg>
  );
}

/** Shared ink line-art icon frame — stroke only, one weight, no fill. */
function ThumbIcon({ children, label }: { children: ReactNode; label: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={40}
      height={40}
      fill="none"
      stroke="var(--ink)"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <title>{label}</title>
      {children}
    </svg>
  );
}

function MagnetIcon() {
  return (
    <ThumbIcon label="Car magnet">
      <rect x={5} y={13} width={38} height={22} rx={9} />
      <path d="M14 24h20" stroke="var(--accent)" />
    </ThumbIcon>
  );
}

function DoorHangerIcon() {
  return (
    <ThumbIcon label="Door hanger">
      <path d="M24 5a6 6 0 0 1 6 6v3H18v-3a6 6 0 0 1 6-6z" />
      <rect x={11} y={14} width={26} height={29} rx={2} />
      <path d="M17 24h14M17 30h14M17 36h9" stroke="var(--accent)" />
    </ThumbIcon>
  );
}

function YardSignIcon() {
  return (
    <ThumbIcon label="Yard sign">
      <rect x={6} y={8} width={36} height={22} rx={2} />
      <path d="M17 30v9M31 30v9M13 43h22" stroke="var(--ink)" />
      <path d="M13 19h22" stroke="var(--accent)" />
    </ThumbIcon>
  );
}

function StaticPackFallback({
  businessName,
  tradeName,
  city,
  owner,
}: {
  businessName: string;
  tradeName?: string;
  city?: string;
  owner?: string;
}) {
  const eyebrowLine = [tradeName, city].filter(Boolean).join(' · ');
  return (
    <div className="wo-pack" aria-label={`Welcome pack preview${owner ? `, prepared for ${owner}` : ''}`}>
      <div className="wo-pack-cards">
        <div className="wo-pack-card">
          <span className="wo-pack-card-eyebrow">{eyebrowLine || 'Your trade · your city'}</span>
          <span className="wo-pack-card-name">{businessName}</span>
          <span className="wo-pack-card-rule" aria-hidden="true" />
          <span className="wo-pack-card-foot">Business card · front</span>
        </div>
        <div className="wo-pack-card wo-pack-card--back">
          <span className="wo-pack-qr">
            <QrGlyph />
          </span>
          <span className="wo-pack-card-caption">Scan to leave a review</span>
          <span className="wo-pack-card-foot">Business card · back</span>
        </div>
      </div>
      <p className="wo-pack-cards-label">2 × 250 business cards</p>

      <div className="wo-pack-kit">
        <div className="wo-pack-thumb">
          <span className="wo-pack-thumb-icons">
            <span className="wo-pack-thumb-icon">
              <MagnetIcon />
            </span>
          </span>
          <span className="wo-pack-thumb-label">1 car magnet</span>
        </div>
        <div className="wo-pack-thumb wo-pack-thumb--pair">
          <span className="wo-pack-thumb-icons">
            <span className="wo-pack-thumb-icon">
              <DoorHangerIcon />
            </span>
            <span className="wo-pack-thumb-icon">
              <YardSignIcon />
            </span>
          </span>
          <span className="wo-pack-thumb-label">Door hanger · yard sign · PDF</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The section                                                         */
/* ------------------------------------------------------------------ */

export default function WelcomePackSection({
  token,
  apiBase,
  isDemo,
  businessName,
  tradeName,
  city,
  domain,
  owner,
}: Props) {
  // Paint immediately — nothing flashes while the real identity (if any) is
  // in flight — and swap to the real one once it lands. A demo shows the
  // sample brand; a real invite shows the prospect's own name in ink.
  const base = apiBase ?? (token ? `/api/invite/${token}` : undefined);
  const [identity, setIdentity] = useState<SiteIdentity>(() =>
    isDemo || !base ? SAMPLE_PACK_IDENTITY : inviteIdentity({ businessName, tradeName, city, domain }),
  );
  const [isReal, setIsReal] = useState(false);

  // Keep the unbranded fallback in step with the invite (the caller resolves
  // trade/city/domain asynchronously) until the live identity replaces it.
  useEffect(() => {
    if (isDemo || !base || isReal) return;
    setIdentity(inviteIdentity({ businessName, tradeName, city, domain }));
  }, [isDemo, base, isReal, businessName, tradeName, city, domain]);

  useEffect(() => {
    if (isDemo || !base) return; // no bundle behind a demo token; sample stays
    let active = true;
    let timer: number | undefined;

    const attempt = (n: number) => {
      axios
        .get(`${base}/identity`)
        .then(res => {
          const data = res.data?.data as SiteIdentity | undefined;
          if (!active) return;
          if (data?.business?.name) {
            setIdentity(data);
            setIsReal(true);
          } else if (n < IDENTITY_RETRY_MS.length) {
            timer = window.setTimeout(() => attempt(n + 1), IDENTITY_RETRY_MS[n]);
          }
        })
        .catch(() => {
          // 404 while the bundle is still landing on R2, 503 while the
          // backend is mid-swap, a flaky edge — all worth a few more tries.
          if (active && n < IDENTITY_RETRY_MS.length) {
            timer = window.setTimeout(() => attempt(n + 1), IDENTITY_RETRY_MS[n]);
          }
        });
    };
    attempt(0);

    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [base, isDemo]);

  const business = useMemo(() => identityToPack(identity, owner ?? ''), [identity, owner]);
  const plan = PLANS.find(p => p.id === INCLUDED_FROM) ?? PLANS[0];
  const higher = PLANS.filter(p => PLANS.indexOf(p) > PLANS.indexOf(plan));
  const tiers = [plan.name, ...higher.map(p => p.name)].join(' and ');

  return (
    /* A perk inside the pricing chapter: hairline on top, copy beside the
       pack pieces. */
    <div className="mt-14 border-t border-zinc-300/70 pt-10">
      <div className="grid items-center gap-x-12 gap-y-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,640px)]">
        <div className="max-w-xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
            Included with {tiers}
          </div>
          <h3 className="mt-3 font-display text-2xl font-bold tracking-[-0.02em] sm:text-3xl">
            A welcome pack, in the same brand as the website.
          </h3>
          <p className="mt-4 text-[16px] leading-relaxed text-zinc-600">
            Business cards and a car magnet arrive printed, carrying the logo,
            colour and type your site wears — and the code on the card&apos;s
            back opens your review page, so a happy customer can say so
            without typing anything.
          </p>
          <p className="mt-3 text-[15px] leading-relaxed text-zinc-500">
            The rest of the kit — door hangers, a yard sign, a car magnet —
            comes as print-ready PDF files in the same brand: take them to any
            print shop and run as many as you like.
          </p>
          <p className="mt-5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
            Ships in your brand · not a stock mockup
          </p>
        </div>

        <PackErrorBoundary
          fallback={
            <StaticPackFallback
              businessName={businessName}
              tradeName={tradeName}
              city={city}
              owner={owner}
            />
          }
        >
          <LivePack b={business} isReal={isReal} />
        </PackErrorBoundary>
      </div>
    </div>
  );
}
